import {
  AI_WORKING_HOURS,
  type AiFailedChatDto,
  type AiHealthDto,
  type AiProviderProblem,
  DEFAULT_TIME_ZONE,
  type LeadAiStatusDto,
  zonedDateString,
} from "@repo/polaris-shared";

/** Longer than the quiet time after a message: the proposal waits for the working hours. */
const NIGHT_WAIT_MS = 10 * 60_000;

const hours = ({ hour, minute }: { hour: number; minute: number }) =>
  `${hour}:${String(minute).padStart(2, "0")}`;

/** "alle 8:30" today, "domani alle 8:30" after midnight. */
function startsAtText(startsAt: number, now: number, timeZone: string): string {
  const time = new Intl.DateTimeFormat("it-IT", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  }).format(startsAt);
  const sameDay =
    zonedDateString(new Date(startsAt), timeZone) === zonedDateString(new Date(now), timeZone);
  return sameDay ? `alle ${time}` : `domani alle ${time}`;
}

/** The line under the last message (ADR 0072): what the AI is doing for this lead. */
export function aiStatusText(
  status: LeadAiStatusDto,
  options: { canManage: boolean; timeZone?: string; now?: number },
): { title: string; detail: string | null } {
  if (status.state === "scheduled") {
    const now = options.now ?? Date.now();
    const startsAt = status.startsAt ? Date.parse(status.startsAt) : Number.NaN;
    // Out of the working hours the automatic proposal waits for the morning (ADR 0077).
    if (startsAt - now > NIGHT_WAIT_MS)
      return {
        title: `L'AI preparerà una proposta ${startsAtText(startsAt, now, options.timeZone ?? DEFAULT_TIME_ZONE)}`,
        detail: `Fuori orario (${hours(AI_WORKING_HOURS.start)}–${hours(AI_WORKING_HOURS.end)}) le proposte automatiche aspettano il mattino.`,
      };
    return { title: "L'AI preparerà una proposta tra poco", detail: null };
  }
  if (status.state === "running")
    return { title: "L'AI sta preparando una proposta…", detail: null };
  if (status.state === "skipped")
    return { title: "L'AI non ha preparato una risposta", detail: status.reason };
  // Credit and key are the owner's: the banner tells them, the others pass it on.
  const forEveryone = status.problem === "quota" || status.problem === "key";
  const hint = forEveryone
    ? options.canManage
      ? "Vale per tutte le chat: vedi l'avviso in alto."
      : "Vale per tutte le chat: avvisa il titolare."
    : null;
  return {
    title: "L'AI non è riuscita a preparare la proposta",
    detail: [status.reason, hint].filter(Boolean).join(" ") || null,
  };
}

const PROBLEM_TEXT: Record<AiProviderProblem, { title: string; action: string }> = {
  quota: {
    title: "Proposte AI ferme: credito OpenAI esaurito",
    action: "Ricarica su platform.openai.com: le proposte ripartono con i prossimi messaggi.",
  },
  key: {
    title: "Proposte AI ferme: OpenAI non accetta la chiave di Polaris",
    action: "Controlla la chiave su platform.openai.com.",
  },
  rate_limit: {
    title: "OpenAI respinge le richieste per troppe chiamate",
    action: "Parte delle proposte AI non arriva; di solito passa da sola.",
  },
  unavailable: {
    title: "OpenAI non risponde",
    action: "Le proposte AI non arrivano; di solito è un guasto di OpenAI e passa da solo.",
  },
};

/** Where the owner fixes it. */
export const PROBLEM_LINK: Partial<Record<AiProviderProblem, { href: string; label: string }>> = {
  quota: {
    href: "https://platform.openai.com/settings/organization/billing/overview",
    label: "Apri la fatturazione OpenAI",
  },
  key: { href: "https://platform.openai.com/api-keys", label: "Apri le chiavi OpenAI" },
  unavailable: { href: "https://status.openai.com", label: "Stato di OpenAI" },
};

/**
 * The banner of owners and admins; null when everything works and no chat
 * waits. `since` reads a time as "dalle 15:29" or "dal 18/10, 15:29". One chat
 * is named and linked; more open their list (owner, 2026-10-22).
 */
export function aiHealthBannerText(
  health: AiHealthDto,
  since: (value: string) => string,
): {
  tone: "danger" | "warning";
  text: string;
  /** What to do: left out on the phone, where the links say it. */
  hint: string | null;
  /** The only chat left behind: the text follows "La chat con <nome>". */
  chat: AiFailedChatDto | null;
  /** "Vedi quali": the list of the chats left behind, each with Apri, Riprova and Ignora. */
  listLabel: string | null;
  retryLabel: string | null;
} | null {
  const chats = health.failedLeads === 1 ? "1 chat" : `${health.failedLeads} chat`;
  const only = health.failedLeads === 1 ? (health.failedChats[0] ?? null) : null;
  if (health.problem) {
    const problem = PROBLEM_TEXT[health.problem.kind];
    return {
      tone: "danger",
      text: `${problem.title} ${since(health.problem.since)}.`,
      hint: problem.action,
      chat: null,
      listLabel:
        health.failedLeads === 0 ? null : only ? "Vedi la chat" : `Vedi le ${health.failedLeads}`,
      retryLabel: health.failedLeads > 0 ? `Riprova ${chats}` : null,
    };
  }
  if (health.failedLeads === 0) return null;
  if (only)
    return {
      tone: "warning",
      text: "è rimasta senza proposta AI per un errore.",
      hint: null,
      chat: only,
      listLabel: null,
      retryLabel: "Riprova",
    };
  return {
    tone: "warning",
    text: `${chats} ${health.failedLeads === 1 ? "è rimasta" : "sono rimaste"} senza proposta AI per un errore.`,
    hint: null,
    chat: null,
    listLabel: "Vedi quali",
    retryLabel: "Riprova tutte",
  };
}
